import { Download, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { EInvoiceChip } from '@/components/einvoicing/einvoice-chip';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ApiError } from '@/lib/api';
import { useMe } from '@/lib/clients';
import {
  canResubmit,
  describeEvent,
  downloadXml,
  formatDateTime,
  isLocked,
  useEInvoice,
  useResubmit,
  type EInvoiceDetail,
} from '@/lib/einvoicing';
import { useOrgId } from '@/lib/org';
import { ltr } from '@/lib/arabic';
import { i18n, useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * E-invoice strip on an approved invoice or credit note (document 06 "E-invoice on invoices"):
 * status, ASP reference, what went wrong, and the actions Timeline, Download XML and Resubmit.
 * Renders nothing while e-invoicing is off or the document has no e-invoice.
 */
export function EInvoicePanel({ documentId }: { documentId: string; label: string }) {
  const t = useT();
  const q = useEInvoice(documentId);
  const [open, setOpen] = useState(false);
  const s = q.data?.submission;
  if (!q.data?.enabled || !s) return null;
  const latest = q.data.payloads[0];
  return (
    <div
      data-testid="einvoice-panel"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-md border px-4 py-2.5 text-sm',
        s.status === 'rejected' || s.needsAttention
          ? 'border-destructive/30 bg-destructive/5'
          : 'bg-card',
      )}
    >
      <span className="font-medium">{t('taxPages.einvoice.panel.title')}</span>
      <EInvoiceChip status={s.status} needsAttention={s.needsAttention} />
      <span className="min-w-48 flex-1 text-muted-foreground">
        {summary(q.data)}
        {isLocked(s)
          ? ` ${
              s.kind === 'credit_note'
                ? t('taxPages.einvoice.panel.lockedCreditNote')
                : t('taxPages.einvoice.panel.lockedInvoice')
            }`
          : ''}
      </span>
      <Button variant="link" size="sm" className="px-1" onClick={() => setOpen(true)}>
        {t('taxPages.einvoice.panel.timeline')}
      </Button>
      {latest ? <XmlButton payload={latest} number={s.number} /> : null}
      <ResubmitButton detail={q.data} documentId={documentId} />
      <TimelineSheet open={open} onOpenChange={setOpen} detail={q.data} documentId={documentId} />
    </div>
  );
}

function summary(d: EInvoiceDetail): string {
  const s = d.submission!;
  const ref = ltr(s.aspReference ?? '');
  switch (s.status) {
    case 'not_required':
      return s.statusReason ?? i18n.t('taxPages.einvoice.panel.notRequired');
    case 'accepted':
      return i18n.t('taxPages.einvoice.panel.accepted', { reference: ref });
    case 'submitted':
      return i18n.t('taxPages.einvoice.panel.submitted', { reference: ref });
    case 'rejected':
      return `${s.errors.map((e) => e.message).join('; ') || i18n.t('taxPages.einvoice.panel.seeTimeline')}.`;
    default:
      return s.needsAttention
        ? i18n.t('taxPages.einvoice.panel.needsAttention', {
            count: s.attempts,
            error: s.lastError ?? i18n.t('taxPages.einvoice.providerUnavailable'),
          })
        : s.attempts > 0
          ? s.nextAttemptAt
            ? i18n.t('taxPages.einvoice.panel.retryingAt', {
                when: ltr(formatDateTime(s.nextAttemptAt)),
              })
            : i18n.t('taxPages.einvoice.panel.retrying')
          : i18n.t('taxPages.einvoice.panel.queued');
  }
}

function XmlButton({
  payload,
  number,
}: {
  payload: { id: string; version: number };
  number: string;
}) {
  const orgId = useOrgId();
  const t = useT();
  return (
    <Button
      variant="link"
      size="sm"
      className="gap-1 px-1"
      onClick={() =>
        void downloadXml(orgId!, payload, `${number}-v${payload.version}.xml`).catch((e: unknown) =>
          toast(e instanceof Error ? e.message : String(e), { tone: 'error' }),
        )
      }
    >
      <Download className="size-3.5" /> {t('taxPages.einvoice.panel.downloadXml')}
    </Button>
  );
}

function ResubmitButton({ detail, documentId }: { detail: EInvoiceDetail; documentId: string }) {
  const me = useMe();
  const t = useT();
  const resubmit = useResubmit();
  const s = detail.submission!;
  if (!me.data?.isStaff || !canResubmit(s)) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      className="gap-1"
      disabled={resubmit.isPending}
      onClick={() =>
        resubmit.mutate(documentId, {
          onSuccess: () =>
            toast(
              s.status === 'rejected'
                ? t('taxPages.einvoice.panel.toastResubmitted', { number: ltr(s.number) })
                : t('taxPages.einvoice.panel.toastRequeued', { number: ltr(s.number) }),
            ),
          onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
        })
      }
    >
      <RotateCw className="size-3.5" />{' '}
      {s.status === 'rejected'
        ? t('taxPages.einvoice.resubmit')
        : t('taxPages.einvoice.retryDelivery')}
    </Button>
  );
}

/** Drawer with every event, the errors of each version and the XML of every version. */
function TimelineSheet({
  open,
  onOpenChange,
  detail,
  documentId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  detail: EInvoiceDetail;
  documentId: string;
}) {
  const t = useT();
  const s = detail.submission!;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {s.kind === 'credit_note'
              ? t('taxPages.einvoice.timeline.titleCreditNote', { number: s.number })
              : t('taxPages.einvoice.timeline.titleInvoice', { number: s.number })}
            <EInvoiceChip status={s.status} needsAttention={s.needsAttention} />
          </SheetTitle>
          <SheetDescription>
            {s.aspReference
              ? `${t('taxPages.einvoice.timeline.reference', { reference: ltr(s.aspReference) })} `
              : ''}
            {t('taxPages.einvoice.timeline.description')}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-6 px-4 pb-6">
          {s.errors.length ? (
            <section className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <h3 className="mb-1 font-medium">
                {s.errors[0]!.source === 'validation'
                  ? t('taxPages.einvoice.timeline.fixAndResubmit')
                  : t('taxPages.einvoice.timeline.providerSaid')}
              </h3>
              <ul className="list-disc space-y-1 ps-4">
                {s.errors.map((e, i) => (
                  <li key={i}>
                    {e.message}
                    {e.rule || e.code ? (
                      <span className="text-muted-foreground">
                        {' '}
                        ({e.rule ?? e.code}
                        {e.term ? `, ${e.term}` : ''})
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <div className="mt-3">
                <ResubmitButton detail={detail} documentId={documentId} />
              </div>
            </section>
          ) : null}
          <section>
            <h3 className="mb-3 text-sm font-semibold">{t('taxPages.einvoice.panel.timeline')}</h3>
            <ol
              className="relative flex flex-col gap-4 border-s ps-5"
              data-testid="einvoice-timeline"
            >
              {[...detail.events].reverse().map((e) => {
                const d = describeEvent(e);
                return (
                  <li key={e.id} className="relative text-sm">
                    <span
                      aria-hidden
                      className={cn(
                        'absolute top-1.5 -start-[25px] size-2.5 rounded-full border-2 border-card',
                        e.event === 'accepted'
                          ? 'bg-success'
                          : e.event === 'rejected' ||
                              e.event === 'validation_failed' ||
                              e.event === 'delivery_failed'
                            ? 'bg-destructive'
                            : 'bg-primary',
                      )}
                    />
                    <div className="font-medium">{d.title}</div>
                    <div className="text-xs text-muted-foreground tnum">{formatDateTime(e.at)}</div>
                    {d.text ? <p className="mt-0.5 text-muted-foreground">{d.text}</p> : null}
                  </li>
                );
              })}
            </ol>
          </section>
          {detail.payloads.length ? (
            <section>
              <h3 className="mb-2 text-sm font-semibold">
                {t('taxPages.einvoice.timeline.versions')}
              </h3>
              <ul className="divide-y rounded-md border text-sm">
                {detail.payloads.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1">
                      <div className="font-medium">
                        {t('taxPages.einvoice.timeline.version', { version: p.version })}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.valid
                          ? t('taxPages.einvoice.timeline.builtPassed', {
                              when: formatDateTime(p.builtAt),
                            })
                          : t('taxPages.einvoice.timeline.builtErrors', {
                              when: formatDateTime(p.builtAt),
                              count: p.findings.filter((f) => f.severity === 'error').length,
                            })}
                      </div>
                    </div>
                    <XmlButton payload={p} number={s.number} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
